
import React, { useState, useEffect, useRef } from 'react';
import {
    MdEdit,
    MdPause,
    MdPlayArrow,
    MdMoreVert,
    MdAdd
} from 'react-icons/md';
import './PriorityManagement.css';
import '../pages/Dashboard.css';
import Swal from 'sweetalert2';
import axios from 'axios';
import {
    GET_ALL_PRIORITY_API,
    CREATE_PRIORITY_API,
    UPDATE_PRIORITY_API
} from '../config/API';
import Loader from '../components/common/Loader';
import Pagination from '../components/common/Pagination';
import TableLimitSelector from '../components/common/TableLimitSelector';
import ActionMenuPortal from '../components/common/ActionMenuPortal';
import { useUser } from '../context/UserContext';

const PriorityManagement = () => {
    const { hasPermission } = useUser();
    // Priority settings from API
    const [priorities, setPriorities] = useState([]);
    const [loading, setLoading] = useState(true);
    const [currentPage, setCurrentPage] = useState(1);
    const [limit, setLimit] = useState(10);
    const [totalPages, setTotalPages] = useState(1);
    const [totalRecords, setTotalRecords] = useState(0);

    // Dummy quotation data (keeping as requested or until quotation module is ready)
    const [quotations, setQuotations] = useState([
        {
            id: 1,
            quotationNo: "QT-2026-001",
            customerName: "Acme Corporation",
            priority: "High",
            reminderStatus: "Active",
            lastReminder: "2026-01-02 09:30 AM"
        },
        {
            id: 2,
            quotationNo: "QT-2026-002",
            customerName: "Tech Solutions Ltd",
            priority: "Critical",
            reminderStatus: "Active",
            lastReminder: "2026-01-02 10:00 AM"
        },
        {
            id: 3,
            quotationNo: "QT-2026-003",
            customerName: "Global Enterprises",
            priority: "Medium",
            reminderStatus: "Paused",
            lastReminder: "2026-01-01 03:00 PM"
        },
        {
            id: 4,
            quotationNo: "QT-2026-004",
            customerName: "BuildIt Materials",
            priority: "Low",
            reminderStatus: "Active",
            lastReminder: "2026-01-02 08:00 AM"
        }
    ]);

    const fetchPriorities = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('accessToken');
            const response = await axios.get(GET_ALL_PRIORITY_API, {
                params: {
                    page: currentPage,
                    limit: limit
                },
                headers: { Authorization: `Bearer ${token}` }
            });

            if (response.data.status) {
                setPriorities(response.data.data || []);
                setTotalPages(response.data.totalPages || 1);
                setTotalRecords(response.data.totalRecords || 0);
            }
        } catch (error) {
            console.error('Error fetching priorities:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchPriorities();
    }, [currentPage, limit]);

    const handlePageChange = (page) => {
        setCurrentPage(page);
    };

    const handleLimitChange = (newLimit) => {
        setLimit(newLimit);
        setCurrentPage(1);
    };

    const [openMenuId, setOpenMenuId] = useState(null);
    const [anchorRect, setAnchorRect] = useState(null);


    const toggleMenu = (id, e) => {
        e.stopPropagation();
        if (openMenuId === id) {
            setOpenMenuId(null);
            setAnchorRect(null);
        } else {
            setOpenMenuId(id);
            setAnchorRect(e.currentTarget.getBoundingClientRect());
        }
    };

    // Close menu when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (event.target.closest('.swal2-container')) return;
            if (event.target.closest('.action-portal-wrapper')) return;
            if (event.target.closest('.action-menu-btn')) return;

            setOpenMenuId(null);
            setAnchorRect(null);
        };

        document.addEventListener('click', handleClickOutside, true);
        return () => {
            document.removeEventListener('click', handleClickOutside, true);
        };
    }, []);

    const getPriorityBadgeClass = (priority) => {
        switch (priority) {
            case 'Critical': return 'priority-critical';
            case 'High': return 'priority-high';
            case 'Medium': return 'priority-medium';
            case 'Low': return 'priority-low';
            default: return '';
        }
    };

    const handleChangePriority = async (quotationId) => {
        const { value: priority } = await Swal.fire({
            title: 'Change Priority',
            input: 'select',
            inputOptions: priorities
                .filter(p => !p.status || p.status.toLowerCase() === 'active')
                .reduce((acc, curr) => {
                    acc[curr.priorityName] = curr.priorityName;
                    return acc;
                }, {}),
            inputPlaceholder: 'Select priority',
            showCancelButton: true,
            confirmButtonText: 'Change',
            cancelButtonText: 'Cancel',
            buttonsStyling: false,
            customClass: {
                confirmButton: 'swal-confirm-btn',
                cancelButton: 'swal-cancel-btn',
            },
        });

        if (priority) {
            setQuotations(quotations.map(q =>
                q.id === quotationId ? { ...q, priority } : q
            ));
            Swal.fire({
                title: 'Success!',
                text: `Priority changed to ${priority}`,
                icon: 'success',
                timer: 2000,
                showConfirmButton: false
            });
        }
    };

    const handleToggleReminder = (quotationId) => {
        setQuotations(quotations.map(q =>
            q.id === quotationId
                ? { ...q, reminderStatus: q.reminderStatus === 'Active' ? 'Paused' : 'Active' }
                : q
        ));
    };

    const handleAddPriority = async () => {
        const { value: formValues } = await Swal.fire({
            html: `
                <div class="modern-swal-container">
                    <div class="modern-swal-header">
                        <h2>New Priority</h2>
                        <p>Configure a new priority level for your tasks.</p>
                    </div>
                    
                    <div class="modern-form-container">
                        <div class="modern-form-group">
                            <label class="modern-label">
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7M18.5 2.5a2.121 2.121 0 113 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                                Priority Name
                            </label>
                            <input id="priorityName" class="modern-input" placeholder="e.g. Critical Impact">
                        </div>
                        
                        <div class="modern-form-row">
                            <div class="modern-form-group">
                                <label class="modern-label">
                                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M7 7h.01M7 12h.01M7 17h.01M12 7h.01M12 12h.01M12 17h.01M17 7h.01M17 12h.01M17 17h.01"/></svg>
                                    Type
                                </label>
                                <select id="type" class="modern-input modern-select">
                                    <option value="Times">Times</option>
                                    <option value="Every">Every</option>
                                </select>
                            </div>
                            <div class="modern-form-group">
                                <label class="modern-label">
                                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"/></svg>
                                    Repetition
                                </label>
                                <input id="repeatation" type="number" min="1" class="modern-input" placeholder="3">
                            </div>
                        </div>

                        <div class="modern-form-row">
                            <div class="modern-form-group">
                                <label class="modern-label">
                                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/></svg>
                                    Duration
                                </label>
                                <select id="duration" class="modern-input modern-select">
                                    <option value="day">Day</option>
                                    <option value="hour">Hour</option>
                                </select>
                            </div>
                            <div class="modern-form-group">
                                <label class="modern-label">
                                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>
                                    Status
                                </label>
                                <select id="status" class="modern-input modern-select">
                                    <option value="active">Active</option>
                                    <option value="inactive">Inactive</option>
                                </select>
                            </div>
                        </div>
                    </div>
                </div>
            `,
            showCancelButton: true,
            confirmButtonText: 'Create Priority',
            cancelButtonText: 'Cancel',
            buttonsStyling: false,
            customClass: {
                popup: 'modern-swal-popup',
                actions: 'modern-actions',
                confirmButton: 'modern-btn-primary',
                cancelButton: 'modern-btn-ghost',
            },
            didOpen: () => {
                const typeSelect = document.getElementById('type');
                const durationSelect = document.getElementById('duration');
                const dayOption = durationSelect.querySelector('option[value="day"]');
                const hourOption = durationSelect.querySelector('option[value="hour"]');

                const updateDuration = () => {
                    if (typeSelect.value === 'Times') {
                        // If "Times" (One Time) -> Force Day
                        durationSelect.value = 'day';
                        dayOption.disabled = false;
                        hourOption.disabled = true;
                    } else if (typeSelect.value === 'Every') {
                        // If "Every" -> Force Hour
                        durationSelect.value = 'hour';
                        dayOption.disabled = true;
                        hourOption.disabled = false;
                    }
                };

                // Run immediately to set initial state
                updateDuration();

                // Listen for changes
                typeSelect.addEventListener('change', updateDuration);
            },
            preConfirm: () => {
                return {
                    priorityName: document.getElementById('priorityName').value,
                    type: document.getElementById('type').value,
                    repeatation: document.getElementById('repeatation').value,
                    duration: document.getElementById('duration').value,
                    status: document.getElementById('status').value
                };
            }
        });

        if (formValues) {
            try {
                const token = localStorage.getItem('accessToken');
                const response = await axios.post(CREATE_PRIORITY_API, formValues, {
                    headers: { Authorization: `Bearer ${token}` }
                });

                if (response.data.status) {
                    Swal.fire({
                        icon: 'success',
                        title: 'Success!',
                        text: 'Priority created successfully',
                        timer: 2000,
                        showConfirmButton: false,
                        // customClass: { popup: 'modern-swal-popup' }
                    });
                    fetchPriorities();
                } else {
                    Swal.fire('Error', response.data.message || 'Failed to create priority', 'error');
                }
            } catch (error) {
                console.error('Error creating priority:', error);
                const errorMsg = error.response?.data?.message || 'An error occurred while creating priority';
                Swal.fire('Error', errorMsg, 'error');
            }
        }
    };

    const handleUpdateSettings = async (priorityData) => {
        const { value: formValues } = await Swal.fire({
            html: `
                <div class="modern-swal-container">
                    <div class="modern-swal-header">
                        <h2>Update Priority</h2>
                        <p>Modify the settings for this priority level.</p>
                    </div>
                    
                    <div class="modern-form-container">
                        <div class="modern-form-group">
                            <label class="modern-label">
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7M18.5 2.5a2.121 2.121 0 113 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                                Priority Name
                            </label>
                            <input id="priorityName" class="modern-input" value="${priorityData.priorityName}" placeholder="e.g. Critical Impact">
                        </div>
                        
                        <div class="modern-form-row">
                            <div class="modern-form-group">
                                <label class="modern-label">
                                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M7 7h.01M7 12h.01M7 17h.01M12 7h.01M12 12h.01M12 17h.01M17 7h.01M17 12h.01M17 17h.01"/></svg>
                                    Type
                                </label>
                                <select id="type" class="modern-input modern-select">
                                    <option value="Times" ${priorityData.type === 'Times' ? 'selected' : ''}>Times</option>
                                    <option value="Every" ${priorityData.type === 'Every' ? 'selected' : ''}>Every</option>
                                </select>
                            </div>
                            <div class="modern-form-group">
                                <label class="modern-label">
                                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"/></svg>
                                    Repetition
                                </label>
                                <input id="repeatation" value="${priorityData.repeatation}" class="modern-input">
                            </div>
                        </div>

                        <div class="modern-form-row">
                            <div class="modern-form-group">
                                <label class="modern-label">
                                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/></svg>
                                    Duration
                                </label>
                                <select id="duration" class="modern-input modern-select">
                                    <option value="day" ${priorityData.duration === 'day' ? 'selected' : ''}>Day</option>
                                    <option value="hour" ${priorityData.duration === 'hour' ? 'selected' : ''}>Hour</option>
                                </select>
                            </div>
                            <div class="modern-form-group">
                                <label class="modern-label">
                                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>
                                    Status
                                </label>
                                <select id="status" class="modern-input modern-select">
                                    <option value="active" ${priorityData.status === 'active' ? 'selected' : ''}>Active</option>
                                    <option value="inactive" ${priorityData.status === 'inactive' ? 'selected' : ''}>Inactive</option>
                                </select>
                            </div>
                        </div>
                    </div>
                </div>
            `,
            showCancelButton: true,
            confirmButtonText: 'Update Priority',
            cancelButtonText: 'Cancel',
            buttonsStyling: false,
            customClass: {
                popup: 'modern-swal-popup',
                actions: 'modern-actions',
                confirmButton: 'modern-btn-primary',
                cancelButton: 'modern-btn-ghost',
            },
            didOpen: () => {
                const typeSelect = document.getElementById('type');
                const durationSelect = document.getElementById('duration');
                const dayOption = durationSelect.querySelector('option[value="day"]');
                const hourOption = durationSelect.querySelector('option[value="hour"]');

                const updateDuration = () => {
                    if (typeSelect.value === 'Times') {
                        // If "Times" (One Time) -> Force Day
                        durationSelect.value = 'day';
                        dayOption.disabled = false;
                        hourOption.disabled = true;
                    } else if (typeSelect.value === 'Every') {
                        // If "Every" -> Force Hour
                        durationSelect.value = 'hour';
                        dayOption.disabled = true;
                        hourOption.disabled = false;
                    }
                };

                // Run immediately to enforce logic on existing data
                updateDuration();

                // Listen for changes
                typeSelect.addEventListener('change', updateDuration);
            },
            preConfirm: () => {
                return {
                    priorityName: document.getElementById('priorityName').value,
                    type: document.getElementById('type').value,
                    repeatation: document.getElementById('repeatation').value,
                    duration: document.getElementById('duration').value,
                    status: document.getElementById('status').value
                };
            }
        });

        if (formValues) {
            try {
                const token = localStorage.getItem('accessToken');
                const response = await axios.put(UPDATE_PRIORITY_API(priorityData.id), {
                    ...priorityData,
                    ...formValues
                }, {
                    headers: { Authorization: `Bearer ${token}` }
                });

                if (response.data.status) {
                    Swal.fire({
                        icon: 'success',
                        title: 'Updated!',
                        text: 'Priority settings updated',
                        timer: 2000,
                        showConfirmButton: false,
                        // customClass: { popup: 'modern-swal-popup' }
                    });
                    fetchPriorities();
                } else {
                    Swal.fire('Error', response.data.message || 'Failed to update priority', 'error');
                }
            } catch (error) {
                console.error('Error updating priority:', error);
                const errorMsg = error.response?.data?.message || 'An error occurred while updating priority';
                Swal.fire('Error', errorMsg, 'error');
            }
        }
    };

    return (
        <div className="dashboard">
            <div className="priority-header">
                <h2 className="section-title" style={{ margin: 0 }}>Priority Management</h2>
            </div>

            <div className="card-container-priority">
                {loading ? (
                    <div style={{ padding: '40px', display: 'flex', justifyContent: 'center' }}>
                        <Loader text="Fetching priority settings..." />
                    </div>
                ) : (
                    <>
                        <div className="reminder-settings-section">
                            <div className="section-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
                                <h3 style={{ margin: 0 }}>Reminder Frequency Settings</h3>
                                {/* {hasPermission('PRIORITIES', 'create') && (
                                    <button className="add-priority-btn" onClick={handleAddPriority}>
                                        <MdAdd size={20} />
                                        Add Priority
                                    </button>
                                )} */}
                            </div>
                            <div className="settings-grid">
                                {priorities.map((priority) => (
                                    <div key={priority.id} className={`settings-card ${getPriorityBadgeClass(priority.priorityName)}`}>
                                        <div className="settings-card-header">
                                            <span className={`priority-badge ${getPriorityBadgeClass(priority.priorityName)}`}>
                                                {priority.priorityName}
                                            </span>
                                            {/* {hasPermission('PRIORITIES', 'update') && (
                                                <button
                                                    className="action-menu-btn"
                                                    onClick={() => handleUpdateSettings(priority)}
                                                    title="Edit Settings"
                                                >
                                                    <MdEdit size={20} />
                                                </button>
                                            )} */}
                                        </div>
                                        <div className="settings-card-body">
                                            <div className="setting-item">
                                                <span className="setting-label">Reminders:</span>
                                                <span className="setting-value">{priority.repeatation} times/{priority.duration}</span>
                                            </div>
                                            <div className="setting-item">
                                                <span className="setting-label">Type:</span>
                                                <span className="setting-value">{priority.type}</span>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                        {/* <Pagination
                            currentPage={currentPage}
                            totalPages={totalPages}
                            onPageChange={handlePageChange}
                            totalRecords={totalRecords}
                            currentCount={priorities.length}
                            entityName="Priorities"
                         limit={limit} onLimitChange={handleLimitChange} /> */}
                    </>
                )}
            </div>
        </div>
    );
};

export default PriorityManagement;
